import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { McpTransport } from "./transport";
import { ClientPushContext, type ClientPushChannel } from "./clientPush";
import { HostIdentityContext, type HostIdentity } from "./hostIdentity";
import { PluginRealtimeContext, type PluginRealtimeSource } from "./PluginRealtimeContext";

/**
 * React context carrying the {@link McpTransport} the plugin should use to
 * reach the host. `null` is the explicit "not provided" sentinel so the hooks
 * can disambiguate from a transport that was provided but is incidentally
 * falsy in some other dimension.
 */
const ExtensionRuntimeContext = createContext<McpTransport | null>(null);

export interface ExtensionRuntimeProviderProps
{
    transport: McpTransport;
    /**
     * Optional host realtime channel consumed by {@link useClientPushSubscription}.
     * Absent (or `null`) in standalone/mock hosts and hosts that predate client-push,
     * in which case the hook is inert. The host binds this channel to the mounted
     * surface's trusted extension + organisation identity.
     */
    clientPush?: ClientPushChannel | null;
    /**
     * Optional host identity/permission context consumed by {@link useHostIdentity}.
     * Absent (or `null`) in standalone/mock hosts and hosts that predate this seam,
     * in which case the hook is inert. The host binds this to the mounted surface's
     * trusted extension identity and forwards only that extension's own grant.
     */
    identity?: HostIdentity | null;
    /**
     * Optional realtime subscription source supplied by the host.
     *
     * When provided, descendant components can call `usePluginRealtimeSource()`
     * to obtain it and subscribe to push notifications. When omitted (dev/mock
     * environments or plugins that don't need realtime), the context defaults
     * to `null` and consumers no-op cleanly.
     */
    realtime?: PluginRealtimeSource;
    children?: ReactNode;
}

/**
 * Wrap a plugin's React tree so descendant {@link useMcpResource} and
 * {@link useMcpTool} calls resolve a default transport without having to
 * thread it through every component.
 *
 * Hooks still accept a per-call `transport` override, which takes precedence
 * over the context value — useful for tests and for plugins that want to
 * shard work across multiple hosts.
 */
export function ExtensionRuntimeProvider({ transport, clientPush = null, identity = null, realtime, children }: ExtensionRuntimeProviderProps): ReactNode
{
    // Memoise so swapping `children` doesn't churn the context identity.
    const value = useMemo(() => transport, [transport]);
    const push = useMemo(() => clientPush, [clientPush]);
    const id = useMemo(() => identity, [identity]);
    const realtimeValue = useMemo(() => realtime ?? null, [realtime]);
    return (
        <ExtensionRuntimeContext.Provider value={value}>
            <ClientPushContext.Provider value={push}>
                <HostIdentityContext.Provider value={id}>
                    <PluginRealtimeContext.Provider value={realtimeValue}>
                        {children}
                    </PluginRealtimeContext.Provider>
                </HostIdentityContext.Provider>
            </ClientPushContext.Provider>
        </ExtensionRuntimeContext.Provider>
    );
}

/**
 * Internal helper used by the hooks. Returns the explicit override when
 * supplied, otherwise falls back to the context. Throws a deterministic
 * error if neither is available so misconfiguration fails loudly at the
 * first render rather than producing silent no-ops.
 */
export function useExtensionRuntimeTransport(override?: McpTransport): McpTransport
{
    const fromContext = useContext(ExtensionRuntimeContext);
    const resolved = override ?? fromContext;
    if (!resolved)
    {
        throw new Error(
            "No McpTransport available. Wrap your plugin in <ExtensionRuntimeProvider transport={...}> "
            + "or pass `transport` directly to the hook.",
        );
    }
    return resolved;
}

/**
 * Non-throwing variant of {@link useExtensionRuntimeTransport}: returns the
 * resolved transport, or `null` when neither an override nor a provider is
 * present. For optional, fire-and-forget consumers (e.g. an auto-injected
 * org-format sync) that must degrade to a no-op rather than crash a surface that
 * renders without a host transport — a standalone/mock-mode run or an isolated
 * unit test.
 */
export function useOptionalExtensionRuntimeTransport(override?: McpTransport): McpTransport | null
{
    const fromContext = useContext(ExtensionRuntimeContext);
    return override ?? fromContext ?? null;
}
